// Replays: a header (format, seed, players, config) and one short token per action. First line
// "ctf6.1 seed=<n> engine=<version>". Each rules version bump retires older replays (ctf1–ctf5 no longer run).
//   a<unit> activate   n ne se s sw nw move   tl / tr turn   t<unit> tag   pf pick up the flag
//   c claim this cell  u use the outpost  g stand guard  e end activation
//   x drop the flag here, x<dir> (xn, xse, ...) drop it on the hex next to the carrier that way
//   l<unit> long shot at an enemy unit   k<unit>@<cell> blink   sk pass on an effect
//   rs<team> resign (rsA / rsB: that team gives up; it may come on either side's turn)
// Unit codes are squad slot numbers (1 = the unit that starts in goal, 2–5 the others), relative to
// the unit's team: your own for activations and blinks, the enemy's for tags and long shots.

import { DIRECTIONS, Hex } from './board';
import { createGame, applyAction, actorTeam, playerOf, unitById, teamOf } from './rules';
import type { CtfAction, CtfConfig, CtfState, Team, Unit } from './types';

export const REPLAY_FORMAT = 'ctf6.1';

export interface ReplayHeader {
  seed: number;
  engine: string;
  config: CtfConfig;
  players: Record<Team, string>;
}

const DIR_TOKENS = ['se', 'ne', 'n', 'nw', 'sw', 's'];

function dirToken(from: Hex, to: Hex): string {
  const i = DIRECTIONS.findIndex(d => d.q === to.q - from.q && d.r === to.r - from.r);
  if (i < 0) throw new Error(`Not adjacent: ${from.q},${from.r} → ${to.q},${to.r}`);
  return DIR_TOKENS[i];
}

export function unitCode(s: CtfState, u: Unit): string {
  return String(u.slot + 1);
}

function unitByCode(s: CtfState, team: Team, code: string): Unit {
  const u = s.units.find(x => x.team === team && unitCode(s, x) === code);
  if (!u) throw new Error(`No unit ${code} on team ${team}`);
  return u;
}

export function encodeAction(s: CtfState, a: CtfAction): string {
  const code = (id: string) => unitCode(s, unitById(s, id));
  switch (a.type) {
    case 'activate': return 'a' + code(a.unitId);
    case 'move': return dirToken(unitById(s, s.active!.unitId).pos!, a.to);
    case 'turn': return a.dir === 1 ? 'tl' : 'tr';
    case 'tag': return 't' + code(a.targetId);
    case 'pickUp': return 'pf';
    case 'claim': return 'c';
    case 'useOutpost': return 'u';
    case 'guard': return 'g';
    case 'dropFlag': {
      const from = unitById(s, s.active!.unitId).pos!;
      return from.q === a.to.q && from.r === a.to.r ? 'x' : 'x' + dirToken(from, a.to);
    }
    case 'endActivation': return 'e';
    case 'longShot': return 'l' + code(a.targetId);
    case 'blink': return `k${code(a.unitId)}@${a.cell}`;
    case 'skipEffect': return 'sk';
    case 'reroll': return 'rr';
    case 'keepRoll': return 'rk';
    case 'sprint': return 'sp';
    case 'jailbreak': return 'j';
    case 'rally': return `ry@${a.cell}`;
    case 'pass': return 'h' + code(a.toUnitId);
    case 'resign': return `rs${teamOf(s, a.playerId)}`;
  }
}

export function encodeReplay(header: ReplayHeader, actions: CtfAction[]): string {
  const lines = [
    `${REPLAY_FORMAT} seed=${header.seed} engine=${header.engine}`,
    `players ${JSON.stringify(header.players)}`,
    `config ${JSON.stringify(header.config)}`,
  ];
  let s = createGame(header.config, [{ id: 'pA', name: header.players.A }, { id: 'pB', name: header.players.B }], header.seed);
  let line: string[] = [];
  const flush = () => { if (line.length) lines.push(line.join(' ')); line = []; };
  for (const raw of actions) {
    if (raw.type === 'activate') flush();
    // Actions are played as the team to act (replays carry no player ids); a resignation keeps its own team.
    const a = (raw.type === 'resign' ? raw : { ...raw, playerId: playerOf(s, actorTeam(s)!).id }) as CtfAction;
    line.push(encodeAction(s, a));
    const next = applyAction(s, a);
    if (next === s) throw new Error(`Illegal action: ${JSON.stringify(raw)}`);
    s = next;
  }
  flush();
  return lines.join('\n') + '\n';
}

const UNIT = '(\\d+)';
const RE = {
  activate: new RegExp(`^a${UNIT}$`), tag: new RegExp(`^t${UNIT}$`),
  longShot: new RegExp(`^l${UNIT}$`), blink: new RegExp(`^k${UNIT}@(\\d+)$`),
  pass: new RegExp(`^h${UNIT}$`), rally: /^ry@(\d+)$/,
};

export function decodeToken(s: CtfState, token: string): CtfAction {
  if (token === 'rsA' || token === 'rsB') return { type: 'resign', playerId: playerOf(s, token.slice(2) as Team).id };
  const team = actorTeam(s);
  if (!team) throw new Error('The game is already over');
  const playerId = playerOf(s, team).id;
  const enemy: Team = team === 'A' ? 'B' : 'A';
  if (token[0] === 'x' && (token === 'x' || DIR_TOKENS.includes(token.slice(1)))) {
    if (!s.active) throw new Error('No unit is active');
    const from = unitById(s, s.active.unitId).pos!;
    const d = token === 'x' ? { q: 0, r: 0 } : DIRECTIONS[DIR_TOKENS.indexOf(token.slice(1))];
    return { type: 'dropFlag', playerId, to: { q: from.q + d.q, r: from.r + d.r } };
  }
  const i = DIR_TOKENS.indexOf(token);
  if (i >= 0) {
    if (!s.active) throw new Error('No unit is active');
    const from = unitById(s, s.active.unitId).pos!;
    return { type: 'move', playerId, to: { q: from.q + DIRECTIONS[i].q, r: from.r + DIRECTIONS[i].r } };
  }
  switch (token) {
    case 'tl': return { type: 'turn', playerId, dir: 1 };
    case 'tr': return { type: 'turn', playerId, dir: -1 };
    case 'pf': return { type: 'pickUp', playerId };
    case 'c': return { type: 'claim', playerId };
    case 'u': return { type: 'useOutpost', playerId };
    case 'g': return { type: 'guard', playerId };
    case 'e': return { type: 'endActivation', playerId };
    case 'sk': return { type: 'skipEffect', playerId };
    case 'rr': return { type: 'reroll', playerId };
    case 'rk': return { type: 'keepRoll', playerId };
    case 'sp': return { type: 'sprint', playerId };
    case 'j': return { type: 'jailbreak', playerId };
  }
  let m: RegExpMatchArray | null;
  if ((m = token.match(RE.activate))) return { type: 'activate', playerId, unitId: unitByCode(s, team, m[1]).id };
  if ((m = token.match(RE.tag))) return { type: 'tag', playerId, targetId: unitByCode(s, enemy, m[1]).id };
  if ((m = token.match(RE.longShot))) return { type: 'longShot', playerId, targetId: unitByCode(s, enemy, m[1]).id };
  if ((m = token.match(RE.blink))) return { type: 'blink', playerId, unitId: unitByCode(s, team, m[1]).id, cell: Number(m[2]) };
  if ((m = token.match(RE.pass))) return { type: 'pass', playerId, toUnitId: unitByCode(s, team, m[1]).id };
  if ((m = token.match(RE.rally))) return { type: 'rally', playerId, cell: Number(m[1]) };
  throw new Error(`Unknown token "${token}"`);
}

export function parseReplay(text: string): { header: ReplayHeader; tokens: string[] } {
  const lines = text.split(/\r?\n/).map(l => l.trim()).filter(l => l && !l.startsWith('#'));
  const fields = (lines.shift() ?? '').split(/\s+/);
  if (fields[0] !== REPLAY_FORMAT) throw new Error(`Not a current replay (expected "${REPLAY_FORMAT}" on the first line)`);
  const kv: Record<string, string> = {};
  for (const f of fields.slice(1)) { const j = f.indexOf('='); if (j > 0) kv[f.slice(0, j)] = f.slice(j + 1); }
  let players: Record<Team, string> = { A: 'Blue', B: 'Red' };
  let config: CtfConfig | null = null;
  const tokens: string[] = [];
  for (const l of lines) {
    if (l.startsWith('players ')) players = JSON.parse(l.slice(8));
    else if (l.startsWith('config ')) config = JSON.parse(l.slice(7));
    else tokens.push(...l.split(/\s+/));
  }
  if (!config) throw new Error('The replay has no config line');
  return { header: { seed: Number(kv.seed), engine: kv.engine ?? 'unknown', config, players }, tokens };
}

export function runReplay(
  text: string,
  onStep?: (state: CtfState, action: CtfAction, index: number) => void,
) {
  const { header, tokens } = parseReplay(text);
  const initial = createGame(header.config, [{ id: 'pA', name: header.players.A }, { id: 'pB', name: header.players.B }], header.seed);
  let s = initial;
  const actions: CtfAction[] = [];
  for (let i = 0; i < tokens.length; i++) {
    let a: CtfAction;
    try { a = decodeToken(s, tokens[i]); } catch (e) {
      return { header, actions, initial, final: s, error: { index: i, token: tokens[i], message: (e as Error).message } };
    }
    const next = applyAction(s, a);
    if (next === s) return { header, actions, initial, final: s, error: { index: i, token: tokens[i], message: 'Illegal move for this position' } };
    s = next;
    actions.push(a);
    onStep?.(s, a, i);
  }
  return { header, actions, initial, final: s, error: null as null | { index: number; token: string; message: string } };
}
